import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPhoneNumber, 
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, checkConnection } from '../firebaseConfig';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔍 AuthContext - onAuthStateChanged triggered with user:', user);
      if (user) {
        console.log('🔍 AuthContext - User authenticated via Firebase Auth:', user);
        try {
          // Try to get additional data from Firestore (but don't fail if it doesn't work)
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = { ...user, ...userDoc.data() };
            console.log('🔍 AuthContext - User data from Firestore:', userData);
            setUser(userData);
          } else {
            console.log('🔍 AuthContext - No Firestore data, using Firebase user:', user);
            // Store phone number in localStorage as backup
            if (user.phoneNumber) {
              localStorage.setItem('customerPhone', user.phoneNumber);
              console.log('🔍 AuthContext - Stored phone number in localStorage as backup');
            }
            setUser(user);
          }
        } catch (error) {
          console.warn('🔍 AuthContext - Firestore unavailable, using Firebase user:', error);
          // Always fallback to Firebase user if Firestore fails
          // Also store phone number in localStorage as backup
          if (user.phoneNumber) {
            localStorage.setItem('customerPhone', user.phoneNumber);
            console.log('🔍 AuthContext - Stored phone number in localStorage as backup');
          }
          setUser(user);
        }
      } else {
        console.log('🔍 AuthContext - No user authenticated');
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const setupRecaptcha = () => {
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('Recaptcha solved');
        },
      });
      setRecaptchaVerifier(verifier);
      return verifier;
    }
    return recaptchaVerifier;
  };

  // Direct Fast2SMS WhatsApp OTP Integration
  const sendWhatsAppOTPDirect = async (phoneNumber, otp) => {
    try {
      // Format phone number (ensure it starts with +91)
      let formattedNumber = phoneNumber;
      if (!phoneNumber.startsWith('+91')) {
        formattedNumber = phoneNumber.startsWith('91') ? `+${phoneNumber}` : `+91${phoneNumber}`;
      }

      // Fast2SMS API configuration
      const API_KEY = '8n4kQedfONjo1wVxEBKHFcaI3MYs9UhSbJG026R5vmyDritpLgzbCykpM029uaOvLUAd3XVhYGJclHSe';
      const BASE_URL = 'https://www.fast2sms.com/dev/whatsapp';
      const MESSAGE_ID = '6231'; // login otp template
      
      // Generate API URL
      const apiUrl = `${BASE_URL}?authorization=${API_KEY}&message_id=${MESSAGE_ID}&numbers=${formattedNumber}&variables_values=${otp}|10`;

      console.log('📱 Sending WhatsApp OTP to:', formattedNumber);
      console.log('🔗 API URL:', apiUrl.replace(API_KEY, 'HIDDEN_API_KEY'));

      // Send request using fetch
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      console.log('📊 Fast2SMS Response:', data);
      
      if (data && data.return === true) {
        return {
          success: true,
          message: 'OTP sent successfully',
          requestId: data.request_id
        };
      } else {
        throw new Error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('WhatsApp OTP Error:', error);
      throw new Error('Failed to send WhatsApp OTP');
    }
  };

  const sendOTP = async (phoneNumber) => {
    try {
      console.log('📱 AuthContext - Sending WhatsApp OTP to:', phoneNumber);
      
      // Generate OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Send WhatsApp OTP via Direct API
      const result = await sendWhatsAppOTPDirect(phoneNumber, otp);
      
      console.log('✅ AuthContext - WhatsApp OTP sent successfully:', result);
      
      // Store OTP in localStorage for verification
      localStorage.setItem('otp_' + phoneNumber, JSON.stringify({
        otp: otp,
        timestamp: Date.now(),
        expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes
      }));
      
      toast.success(`WhatsApp OTP sent to ${phoneNumber}! Check your WhatsApp messages.`);
      
      // Return a mock confirmation result for compatibility
      return {
        confirm: async (enteredOTP) => {
          return await verifyOTP(null, enteredOTP, phoneNumber);
        }
      };
      
    } catch (error) {
      console.error('❌ AuthContext - Error sending WhatsApp OTP:', error);
      
      // Fallback to demo OTP for development
      console.log('🔄 AuthContext - Falling back to demo OTP');
      
      // Store demo OTP
      localStorage.setItem('otp_' + phoneNumber, JSON.stringify({
        otp: '123456',
        timestamp: Date.now(),
        expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes
      }));
      
      toast.success(`Demo OTP sent to ${phoneNumber}! Use: 123456`);
      
      // Return a mock confirmation result for compatibility
      return {
        confirm: async (enteredOTP) => {
          return await verifyOTP(null, enteredOTP, phoneNumber);
        }
      };
    }
  };

  const verifyOTP = async (confirmationResult, otp, phoneNumber = null) => {
    try {
      console.log('🔐 AuthContext - Verifying WhatsApp OTP:', otp);
      
      // If phoneNumber is provided, use our WhatsApp OTP verification
      if (phoneNumber) {
        // Get stored OTP data
        const storedOTPData = localStorage.getItem('otp_' + phoneNumber);
        
        if (!storedOTPData) {
          throw new Error('OTP not found or expired');
        }
        
        const otpData = JSON.parse(storedOTPData);
        
        // Check if OTP is expired
        if (Date.now() > otpData.expiresAt) {
          localStorage.removeItem('otp_' + phoneNumber);
          throw new Error('OTP has expired');
        }
        
        // Verify OTP
        if (otpData.otp === otp) {
          console.log('✅ AuthContext - WhatsApp OTP verified successfully');
          
          // Clear stored OTP
          localStorage.removeItem('otp_' + phoneNumber);
          
          // Create a mock user object for compatibility
          const mockUser = {
            uid: 'whatsapp_' + Date.now(),
            phoneNumber: phoneNumber,
            displayName: null,
            email: null,
            emailVerified: false,
            isAnonymous: false,
            metadata: {
              creationTime: new Date().toISOString(),
              lastSignInTime: new Date().toISOString()
            }
          };
          
          // Store user in localStorage for session management
          localStorage.setItem('customerPhone', phoneNumber);
          localStorage.setItem('user', JSON.stringify(mockUser));
          
          setUser(mockUser);
          
          toast.success('OTP verified successfully!');
          
          return { user: mockUser };
        } else {
          throw new Error('Invalid OTP');
        }
      }
      
      // Fallback to Firebase OTP verification (for backward compatibility)
      if (confirmationResult) {
        const result = await confirmationResult.confirm(otp);
        const user = result.user;
        
        console.log('🔍 AuthContext - OTP verification successful, user:', user);
        
        // Try to save user data to Firestore (but don't fail if it doesn't work)
        try {
          const userData = {
            uid: user.uid,
            phoneNumber: user.phoneNumber,
            createdAt: new Date(),
            role: 'customer'
          };
          console.log('🔍 AuthContext - Attempting to save user data to Firestore:', userData);
          await setDoc(doc(db, 'users', user.uid), userData, { merge: true });
          console.log('🔍 AuthContext - User data saved to Firestore successfully');
        } catch (firestoreError) {
          console.warn('🔍 AuthContext - Failed to save to Firestore (continuing anyway):', firestoreError);
          // Don't fail the authentication if Firestore save fails
        }

        toast.success('Login successful!');
        return result;
      }
      
      throw new Error('No verification method available');
      
    } catch (error) {
      console.error('❌ AuthContext - Error verifying OTP:', error);
      
      // Fallback to demo verification for development
      if (error.message.includes('OTP not found') || error.message.includes('expired') || error.message.includes('Invalid OTP')) {
        console.log('🔄 AuthContext - Falling back to demo OTP verification');
        
        // Demo OTP verification - accept 123456 or any 6-digit number
        if (otp === '123456' || (otp.length === 6 && /^[0-9]{6}$/.test(otp))) {
          console.log('✅ AuthContext - Demo OTP verified successfully');
          
          // Create a mock user object for demo
          const mockUser = {
            uid: 'demo_' + Date.now(),
            phoneNumber: phoneNumber || '+918107436861',
            displayName: null,
            email: null,
            emailVerified: false,
            isAnonymous: false,
            metadata: {
              creationTime: new Date().toISOString(),
              lastSignInTime: new Date().toISOString()
            }
          };
          
          // Store user in localStorage for session management
          localStorage.setItem('customerPhone', mockUser.phoneNumber);
          localStorage.setItem('user', JSON.stringify(mockUser));
          
          setUser(mockUser);
          
          toast.success('Demo OTP verified successfully!');
          
          return { user: mockUser };
        } else {
          throw new Error('Invalid OTP. Demo OTP is: 123456');
        }
      }
      
      toast.error(error.message || 'Failed to verify OTP. Please try again.');
      throw error;
    }
  };

  const adminLogin = async (email, password) => {
    try {
      console.log('AuthContext adminLogin called with:', email, password);
      
      // For demo purposes, check credentials first
      if (email === 'admin@btechbawarchi.com' && password === 'admin123') {
        console.log('Credentials valid, creating mock admin user...');
        
        // Create a mock admin user for demo
        const mockAdminUser = {
          uid: 'admin-demo-001',
          email: email,
          role: 'admin',
          name: 'B.Tech Bawarchi Admin',
          permissions: ['menu_management', 'order_management', 'discount_management', 'coupon_management']
        };
        
        console.log('Setting user:', mockAdminUser);
        setUser(mockAdminUser);
        toast.success('Admin login successful!');
        return { user: mockAdminUser };
      } else {
        console.log('Invalid credentials provided');
        toast.error('Invalid admin credentials');
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Login failed. Please check your credentials.');
      throw error;
    }
  };

  const adminSignup = async (email, password, adminData) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Save admin data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        role: 'admin',
        ...adminData,
        createdAt: new Date()
      });

      toast.success('Admin account created successfully!');
      return result;
    } catch (error) {
      console.error('Admin signup error:', error);
      toast.error('Failed to create admin account.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Check if it's a mock admin user
      if (user && user.uid === 'admin-demo-001') {
        setUser(null);
        toast.success('Logged out successfully!');
      } else {
        await signOut(auth);
        toast.success('Logged out successfully!');
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout.');
    }
  };

  const value = {
    user,
    loading,
    sendOTP,
    verifyOTP,
    adminLogin,
    adminSignup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <div id="recaptcha-container"></div>
    </AuthContext.Provider>
  );
};
